/**
 * Distribute / Ramp (W2-11 batch-tools; DESIGN.md §5.8, RPE MultiNote / MultiEvent E§3.12 with the E§4.9 errata
 * fixed). Formula and its expression twin: ramp.ts.
 *
 * Items are sorted (notes by time or by X; keys always by time, then by id), n = 1..N, and item n gets
 * `mode(v_n, Z_n)` where Z_n is the ramp value of n (`rampValues`, seeded jitter drawn once per item). Targets:
 *  - notes: X, scroll speed, width, height offset, appear time, opacity (integral, clamped to 0..255; >= 999999 s
 *    appear time = always visible), time (beats snapped to 1/1920; a hold keeps its length);
 *  - keys of numeric tracks: value (arrive and leave; after a Stay only the leave value, the arrive value is the
 *    stayed one), arrive value, leave value, time (keys keep their order on their track; X/Y-linked partner keys
 *    move with them) and curve (Z rounded to an RPE easing id 1..29 for the segment starting at the key; X/Y-linked
 *    partner segments follow, link groups are kept in sync).
 * Every write goes through the motion command machinery (core/commands motionTrack `commitKeys`), so the model
 * invariants hold: holds arrive as jumps, the lead-in key at beat 0, link groups.
 */

import { Ease } from '../ease/index';
import type { Ease as EaseValue } from '../ease/types';
import { Expr } from '../expr/index';
import { RPE_VISIBLE_ALWAYS } from '../model/defaults';
import type { Key, KeyId, Note, NoteId, TrackId, TrackValue } from '../model/types';
import type { RecordPatch, TxBuilder } from '../ops/types';
import { Beats, beatFromF64Snapped } from '../time/beat';
import type { Beat } from '../time/types';
import type { MessageRef } from '../util/types';
import {
  commitKeys,
  easeUseFor,
  enforceKeyRules,
  keyAtBeat,
  linkedNeighbours,
  readKeys,
  requireTrack,
  sameValue,
  setBoth,
  syncLinkGroups,
  tidy,
  xyPartnerTrack,
  type KeyRec,
  type TrackInfo,
} from '../commands/kit';
import { allFinite, EarliestBeat, keyTimeOrder, noteTimeOrder, noteXOrder, toolReason, TOOLS_MSG } from './common';
import { applyMode, rampExpression, rampValues, roundHalfAway } from './ramp';
import type { DistributeParams, DistributeTarget, RampSpec, ToolReport } from './types';

/** Grid (1/TIME_DIV beat) that distributed times are snapped to. */
export const TIME_DIV = 1920;
/** RPE easing ids a curve target may produce. */
export const EASE_ID_MIN = 1;
export const EASE_ID_MAX = 29;

type NoteTarget = Extract<DistributeTarget, { kind: 'notes' }>;
type KeyTarget = Extract<DistributeTarget, { kind: 'keys' }>;

/** The ramp part of the parameters. */
export function rampOf(p: Pick<DistributeParams, 'from' | 'to' | 'curve' | 'pattern' | 'jitter'>): RampSpec {
  return { from: p.from, to: p.to, curve: p.curve, pattern: p.pattern, jitter: p.jitter };
}

/** True when a target edits easing ids (Z is rounded to an id before the mode). */
function isEaseTarget(t: DistributeTarget): boolean {
  return t.kind === 'keys' && t.field === 'easing';
}

/** The expression equivalent of a Distribute configuration ("= lerp(-400, 400, ease(6, u)) * alt(-1, 1)"). */
export function distributeExpression(p: DistributeParams): string {
  return rampExpression(rampOf(p), p.mode, isEaseTarget(p.target) ? (b) => `round(${b})` : undefined);
}

/** Fails unless the numeric parameters are finite. */
function checkParams(tx: TxBuilder, p: DistributeParams): void {
  if (!allFinite([p.from, p.to, p.jitter, p.seed, ...p.pattern])) tx.fail(toolReason(TOOLS_MSG.distributeBadNumber));
}

/** The Z values of `count` items. */
function zValues(p: DistributeParams, count: number): number[] {
  const z = rampValues(rampOf(p), count, Expr.rng(p.seed));
  return isEaseTarget(p.target) ? z.map(roundHalfAway) : z;
}

function finish(tx: TxBuilder, changed: number, notes: MessageRef[], p: DistributeParams): ToolReport {
  for (const n of notes) tx.note(n);
  const expression = distributeExpression(p);
  return notes.length === 0 ? { changed, expression } : { changed, expression, notes };
}

// ---------------------------------------------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------------------------------------------

function noteValue(n: Note, field: NoteTarget['field']): number {
  switch (field) {
    case 'x':
    case 'speed':
    case 'size':
    case 'yOffset':
    case 'alpha':
      return n[field];
    case 'visibleTime':
      return n.visibleTime ?? RPE_VISIBLE_ALWAYS;
    case 'time':
      return Beats.toF64(n.t);
  }
}

function notePatch(field: Exclude<NoteTarget['field'], 'time'>, v: number | null): RecordPatch<Note> {
  switch (field) {
    case 'visibleTime':
      return { visibleTime: v };
    case 'x':
      return { x: v ?? 0 };
    case 'speed':
      return { speed: v ?? 0 };
    case 'size':
      return { size: v ?? 0 };
    case 'yOffset':
      return { yOffset: v ?? 0 };
    case 'alpha':
      return { alpha: v ?? 0 };
  }
}

function distributeNotes(tx: TxBuilder, p: DistributeParams, target: NoteTarget): ToolReport {
  const seen = new Set<NoteId>();
  const notes: Note[] = [];
  for (const id of target.ids) {
    const n = tx.doc.notes.get(id);
    if (n !== undefined && !seen.has(id)) {
      seen.add(id);
      notes.push(n);
    }
  }
  if (notes.length === 0) tx.fail(toolReason(TOOLS_MSG.distributeNothing));
  notes.sort(p.order === 'x' ? noteXOrder : noteTimeOrder);
  const z = zValues(p, notes.length);
  const out: MessageRef[] = [];
  let changed = 0;
  let clamped = 0;
  const focus = new EarliestBeat();
  notes.forEach((n, i) => {
    const raw = applyMode(p.mode, noteValue(n, target.field), z[i] ?? 0);
    if (!Number.isFinite(raw))
      tx.fail(toolReason(TOOLS_MSG.distributeEvalFailed, { index: i + 1, count: notes.length }));
    if (target.field === 'time') {
      const t = beatFromF64Snapped(raw, TIME_DIV, tx.rounding);
      if (Beats.cmp(t, Beats.ZERO) < 0) tx.fail(toolReason(TOOLS_MSG.retimeBeforeStart));
      if (Beats.eq(t, n.t)) return;
      const dt = Beats.sub(t, n.t, tx.rounding);
      const end = n.kind === 'hold' ? Beats.add(n.end, dt, tx.rounding) : t;
      tx.update('notes', n.id, { t, end });
      changed++;
      focus.add(t);
      return;
    }
    let v: number | null = tidy(raw);
    if (target.field === 'alpha') {
      const a = Math.min(255, Math.max(0, Math.round(v)));
      if (a !== Math.round(v)) clamped++;
      v = a;
    } else if (target.field === 'visibleTime') {
      if (v < 0) {
        clamped++;
        v = 0;
      }
      if (v >= RPE_VISIBLE_ALWAYS) v = null;
    }
    const before = target.field === 'visibleTime' ? n.visibleTime : n[target.field];
    if (v === before) return;
    tx.update('notes', n.id, notePatch(target.field, v));
    changed++;
    focus.add(n.t);
  });
  if (clamped > 0) out.push(toolReason(TOOLS_MSG.distributeClamped, { count: clamped }));
  const firstLine = notes[0]?.line;
  if (focus.beat !== null)
    tx.setFocus(firstLine === undefined ? { beat: focus.beat } : { beat: focus.beat, line: firstLine });
  return finish(tx, changed, out, p);
}

// ---------------------------------------------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------------------------------------------

/** Selected key indices of a track's records, ascending. */
function indicesOf(keys: readonly KeyRec[], ids: ReadonlySet<KeyId>): number[] {
  const out: number[] = [];
  keys.forEach((k, i) => {
    if (k.id !== null && ids.has(k.id)) out.push(i);
  });
  return out;
}

/**
 * Applies a value edit to key i (same rules as the key value commands): 'both' keeps the key continuous, except
 * after a Stay where the arrive value is the stayed value (only the leave value changes); 'in' is refused after a
 * Stay; the first key never jumps; the last key of a GIF track never jumps. Returns false when refused.
 */
function applyKeyValue(
  keys: KeyRec[],
  i: number,
  part: 'both' | 'in' | 'out',
  v: TrackValue,
  info: TrackInfo,
): boolean {
  const k = keys[i];
  if (k === undefined) return false;
  const prev = keys[i - 1];
  const afterHold = prev !== undefined && prev.ease.kind === 'hold';
  if (i === 0 || (info.prop === 'gif' && i === keys.length - 1)) {
    if (i !== 0 && afterHold) return false;
    k.vIn = v;
    k.vOut = v;
  } else if (part === 'both') {
    setBoth(keys, i, v);
  } else if (part === 'in') {
    if (afterHold) return false;
    k.vIn = v;
  } else {
    k.vOut = v;
  }
  const next = keys[i + 1];
  if (k.ease.kind === 'hold' && next !== undefined) next.vIn = k.vOut;
  return true;
}

interface KeyItem {
  readonly key: Key;
  readonly info: TrackInfo;
}

/** Existing keys of numeric line/variable tracks, deduplicated, in time order; counts the others. */
function keyItems(tx: TxBuilder, ids: readonly KeyId[]): { items: KeyItem[]; skipped: number } {
  const seen = new Set<KeyId>();
  const items: KeyItem[] = [];
  let skipped = 0;
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    const key = tx.doc.keys.get(id);
    if (key === undefined) continue;
    const info = requireTrack(tx, key.track);
    if (info.valueType !== 'number') {
      skipped++;
      continue;
    }
    items.push({ key, info });
  }
  items.sort((a, b) => keyTimeOrder(a.key, b.key));
  return { items, skipped };
}

function groupItems(items: readonly KeyItem[]): Map<TrackId, KeyItem[]> {
  const out = new Map<TrackId, KeyItem[]>();
  for (const it of items) {
    const list = out.get(it.key.track);
    if (list === undefined) out.set(it.key.track, [it]);
    else list.push(it);
  }
  return out;
}

function keyCurrent(k: Key, field: KeyTarget['field']): number {
  switch (field) {
    case 'value':
    case 'valueOut':
      return k.vOut as number;
    case 'valueIn':
      return k.vIn as number;
    case 'time':
      return Beats.toF64(k.t);
    case 'easing':
      return k.ease.kind === 'tween' ? k.ease.id : 1;
  }
}

function focusBeat(tx: TxBuilder, beats: readonly Beat[], items: readonly KeyItem[]): void {
  let best: Beat | null = null;
  for (const b of beats) if (best === null || Beats.lt(b, best)) best = b;
  if (best === null) return;
  const line = items[0]?.info.line?.id;
  tx.setFocus(line === undefined ? { beat: best } : { beat: best, line });
}

function distributeKeyValues(
  tx: TxBuilder,
  p: DistributeParams,
  items: readonly KeyItem[],
  z: readonly number[],
  field: 'value' | 'valueIn' | 'valueOut',
): { changed: number; notes: MessageRef[] } {
  const part = field === 'value' ? 'both' : field === 'valueIn' ? 'in' : 'out';
  const values = new Map<KeyId, number>();
  items.forEach((it, i) => {
    const v = applyMode(p.mode, keyCurrent(it.key, field), z[i] ?? 0);
    if (!Number.isFinite(v)) tx.fail(toolReason(TOOLS_MSG.distributeEvalFailed, { index: i + 1, count: items.length }));
    values.set(it.key.id, tidy(v));
  });
  let changed = 0;
  let refused = 0;
  const touched: KeyId[] = [];
  for (const [trackId, list] of groupItems(items)) {
    const info = list[0]?.info ?? requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    const before = keys.map((k) => ({ vIn: k.vIn, vOut: k.vOut }));
    for (const i of indicesOf(keys, new Set(list.map((it) => it.key.id)))) {
      const id = (keys[i] as KeyRec).id as KeyId;
      const v = values.get(id);
      if (v !== undefined && !applyKeyValue(keys, i, part, v, info)) refused++;
    }
    enforceKeyRules(keys, info);
    keys.forEach((k, i) => {
      const b = before[i];
      if (b !== undefined && (!sameValue(b.vIn, k.vIn) || !sameValue(b.vOut, k.vOut))) {
        changed++;
        if (k.id !== null) touched.push(k.id);
      }
    });
    commitKeys(tx, info, keys);
  }
  syncLinkGroups(
    tx,
    touched.flatMap((id) => linkedNeighbours(tx, id)),
  );
  const notes: MessageRef[] = [];
  if (refused > 0) notes.push(toolReason(TOOLS_MSG.distributeArriveAfterStay, { count: refused }));
  return { changed, notes };
}

/** Beats per key after the time distribution (X/Y-linked partner keys follow). */
function distributeKeyTimes(
  tx: TxBuilder,
  p: DistributeParams,
  items: readonly KeyItem[],
  z: readonly number[],
): { changed: number; beats: Beat[] } {
  const target = new Map<KeyId, Beat>();
  items.forEach((it, i) => {
    const raw = applyMode(p.mode, keyCurrent(it.key, 'time'), z[i] ?? 0);
    if (!Number.isFinite(raw))
      tx.fail(toolReason(TOOLS_MSG.distributeEvalFailed, { index: i + 1, count: items.length }));
    const t = beatFromF64Snapped(raw, TIME_DIV, tx.rounding);
    if (Beats.cmp(t, Beats.ZERO) < 0) tx.fail(toolReason(TOOLS_MSG.retimeBeforeStart));
    target.set(it.key.id, t);
  });
  // X/Y-linked partners: the other axis' key at the same beat moves to the same new beat.
  for (const it of items) {
    const partner = xyPartnerTrack(tx, it.info);
    if (partner === undefined) continue;
    const pk = keyAtBeat(tx, partner, it.key.t);
    const t = target.get(it.key.id);
    if (pk !== undefined && t !== undefined && !target.has(pk)) target.set(pk, t);
  }
  const byTrack = new Map<TrackId, KeyId[]>();
  for (const id of target.keys()) {
    const k = tx.doc.keys.get(id);
    if (k === undefined) continue;
    const list = byTrack.get(k.track);
    if (list === undefined) byTrack.set(k.track, [id]);
    else list.push(id);
  }
  let changed = 0;
  const beats: Beat[] = [];
  for (const [trackId, ids] of byTrack) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    for (const i of indicesOf(keys, new Set(ids))) {
      const k = keys[i] as KeyRec;
      const t = target.get(k.id as KeyId);
      if (t === undefined || Beats.eq(t, k.t)) continue;
      k.t = t;
      changed++;
      beats.push(t);
    }
    for (let i = 1; i < keys.length; i++) {
      if (!Beats.lt((keys[i - 1] as KeyRec).t, (keys[i] as KeyRec).t)) tx.fail(toolReason(TOOLS_MSG.retimeCollide));
    }
    commitKeys(tx, info, keys);
  }
  return { changed, beats };
}

/** The ease a curve id gives a segment: the whole curve, or the old slice when it is still valid for the new id. */
function easeForId(id: number, current: EaseValue): Extract<EaseValue, { kind: 'tween' }> {
  if (current.kind === 'tween' && (current.left !== 0 || current.right !== 1)) {
    if (current.left < current.right && !Ease.sliceIsDegenerate(id, current.left, current.right)) {
      return { kind: 'tween', id, left: current.left, right: current.right };
    }
  }
  return { kind: 'tween', id, left: 0, right: 1 };
}

function distributeKeyEases(
  tx: TxBuilder,
  p: DistributeParams,
  items: readonly KeyItem[],
  z: readonly number[],
): { changed: number; notes: MessageRef[]; beats: Beat[] } {
  const wanted = new Map<KeyId, number>();
  let clamped = 0;
  let lastKeys = 0;
  items.forEach((it, i) => {
    if (tx.idx.nextKey(it.key.id) === undefined) {
      lastKeys++;
      return;
    }
    const raw = applyMode(p.mode, keyCurrent(it.key, 'easing'), z[i] ?? 0);
    if (!Number.isFinite(raw))
      tx.fail(toolReason(TOOLS_MSG.distributeEvalFailed, { index: i + 1, count: items.length }));
    const r = roundHalfAway(raw);
    const id = Math.min(EASE_ID_MAX, Math.max(EASE_ID_MIN, r));
    if (id !== r) clamped++;
    wanted.set(it.key.id, id);
    const partner = xyPartnerTrack(tx, it.info);
    const pk = partner === undefined ? undefined : keyAtBeat(tx, partner, it.key.t);
    if (pk !== undefined && !wanted.has(pk) && tx.idx.nextKey(pk) !== undefined) wanted.set(pk, id);
  });
  let changed = 0;
  let skipped = 0;
  let firstReason: MessageRef | null = null;
  const touched: KeyId[] = [];
  const beats: Beat[] = [];
  const byTrack = new Map<TrackId, KeyId[]>();
  for (const id of wanted.keys()) {
    const k = tx.doc.keys.get(id);
    if (k === undefined) continue;
    const list = byTrack.get(k.track);
    if (list === undefined) byTrack.set(k.track, [id]);
    else list.push(id);
  }
  for (const [trackId, ids] of byTrack) {
    const info = requireTrack(tx, trackId);
    const keys = readKeys(tx, trackId);
    let dirty = false;
    for (const i of indicesOf(keys, new Set(ids))) {
      const k = keys[i] as KeyRec;
      const next = keys[i + 1];
      const id = wanted.get(k.id as KeyId);
      if (next === undefined || id === undefined) continue;
      const ease = easeForId(id, k.ease);
      const why = Ease.validateFor(ease, easeUseFor(tx, info, k.vOut, next.vIn));
      if (why !== null) {
        skipped++;
        firstReason ??= why;
        continue;
      }
      if (
        k.ease.kind === 'tween' &&
        k.ease.id === ease.id &&
        k.ease.left === ease.left &&
        k.ease.right === ease.right
      ) {
        continue;
      }
      // Turning a Stay into a curve glides to the next key's leave value (the Stay's jump goes away).
      if (k.ease.kind === 'hold' && sameValue(next.vIn, k.vOut) && !sameValue(next.vIn, next.vOut))
        next.vIn = next.vOut;
      k.ease = ease;
      dirty = true;
      changed++;
      beats.push(k.t);
      if (k.id !== null) touched.push(k.id);
    }
    if (dirty) commitKeys(tx, info, keys);
  }
  syncLinkGroups(tx, touched);
  const notes: MessageRef[] = [];
  if (lastKeys > 0) notes.push(toolReason(TOOLS_MSG.distributeLastKey, { count: lastKeys }));
  if (clamped > 0) notes.push(toolReason(TOOLS_MSG.distributeClamped, { count: clamped }));
  if (skipped > 0) {
    notes.push(toolReason(TOOLS_MSG.distributeEaseSkipped, { count: skipped }));
    if (firstReason !== null) notes.push(firstReason);
  }
  return { changed, notes, beats };
}

function distributeKeys(tx: TxBuilder, p: DistributeParams, target: KeyTarget): ToolReport {
  const { items, skipped } = keyItems(tx, target.ids);
  if (items.length === 0) {
    tx.fail(toolReason(skipped > 0 ? TOOLS_MSG.distributeNotNumeric : TOOLS_MSG.distributeNothing));
  }
  const z = zValues(p, items.length);
  const notes: MessageRef[] = [];
  if (skipped > 0) notes.push(toolReason(TOOLS_MSG.distributeSkippedKeys, { count: skipped }));
  switch (target.field) {
    case 'value':
    case 'valueIn':
    case 'valueOut': {
      const r = distributeKeyValues(tx, p, items, z, target.field);
      focusBeat(
        tx,
        items.map((it) => it.key.t),
        items,
      );
      return finish(tx, r.changed, [...notes, ...r.notes], p);
    }
    case 'time': {
      const r = distributeKeyTimes(tx, p, items, z);
      focusBeat(tx, r.beats, items);
      return finish(tx, r.changed, notes, p);
    }
    case 'easing': {
      const r = distributeKeyEases(tx, p, items, z);
      focusBeat(tx, r.beats, items);
      return finish(tx, r.changed, [...notes, ...r.notes], p);
    }
  }
}

/** Distribute / Ramp (see the file header). */
export function distribute(tx: TxBuilder, p: DistributeParams): ToolReport {
  checkParams(tx, p);
  return p.target.kind === 'notes' ? distributeNotes(tx, p, p.target) : distributeKeys(tx, p, p.target);
}
